import { cn } from '@repo/ui';
import {
  BarChart3,
  Bell,
  Briefcase,
  Building2,
  History,
  IdCard,
  KeyRound,
  LayoutDashboard,
  LayoutGrid,
  type LucideIcon,
  Package,
  PiggyBank,
  Receipt,
  Settings,
  ShieldCheck,
  ShoppingCart,
  Truck,
  Users,
  Warehouse,
} from 'lucide-react';
import type { ReactElement } from 'react';
import { matchPath, NavLink, useLocation } from 'react-router-dom';
import { useCurrentUser } from '@/features/auth/hooks/useAuthMutations';
import { formatBadge, useNotificationUnreadCount } from '@/hooks/useNotificationUnreadCount';
import { hasPermission, PERMS } from '@/lib/permissions';
import { type NavItem, visibleNavGroups } from './nav-items';

/**
 * Map from icon-name (declared in nav-items.ts) to a lazy import.
 * Sprint 1 — extended with bell / receipt / building-2 /
 * layout-grid / briefcase / piggy-bank for the grouped sidebar.
 * Tree-shaken by Vite (named imports only — see Vercel's
 * `bundle-barrel-imports` rule).
 */
const ICONS: Record<string, LucideIcon> = {
  'layout-dashboard': LayoutDashboard,
  users: Users,
  package: Package,
  'shopping-cart': ShoppingCart,
  'bar-chart-3': BarChart3,
  settings: Settings,
  truck: Truck,
  warehouse: Warehouse,
  'shield-check': ShieldCheck,
  // Sprint 1 — six new icons for the grouped layout.
  bell: Bell,
  receipt: Receipt,
  'building-2': Building2,
  'layout-grid': LayoutGrid,
  briefcase: Briefcase,
  'piggy-bank': PiggyBank,
  // Sprint 3 PR#3a — KeyRound icon for the Roles y Permisos
  // entry in the Sistema group.
  'key-round': KeyRound,
  // Sprint 3 PR#3b — History icon for the Auditoría entry in the
  // Sistema group. The `audit.view` permission + the real
  // AuditLogPage body ship in the same PR.
  history: History,
  // HR expediente — IdCard icon for the Trabajadores entry.
  'id-card': IdCard,
};

/**
 * Returns true when the current path corresponds to the given
 * nav item `to`. The Dashboard item has `end: true` so React
 * Router's `NavLink` only matches the exact `/`. For everything
 * else we want a prefix match: visiting `/inventory/receipts/abc`
 * should keep "Inventario" active even though its `to` is the
 * shorter `/inventory`.
 *
 * `matchPath({ path, end: false }, pathname)` returns null when
 * the path is not a prefix match, so the active background is
 * applied exactly when the user is "inside" the item.
 */
function useIsActive(to: string, end?: boolean): boolean {
  const { pathname } = useLocation();
  if (end) return pathname === to;
  return matchPath({ path: to, end: false }, pathname) !== null;
}

/**
 * Desktop sidebar — fixed left rail at `w-52` (208 px). Hidden below
 * `lg` (mobile uses AppShell's drawer via `mobileOpen` state instead).
 *
 * Sprint 1 WU-1 — renders 5 grouped sections with uppercase
 * `<h3>` headers in the order defined by `NAV_GROUPS`. Each
 * section is rendered conditionally via `visibleNavGroups(user)`,
 * which already filters out empty groups (REQ-1 empty-group rule).
 *
 * The unread-notifications badge (REQ-2) is rendered next to the
 * Notificaciones item's label when the count is > 0.
 */
export function Sidebar(): ReactElement {
  const user = useCurrentUser();
  const groups = visibleNavGroups(user);
  // Sprint 1 B1 — unread badge. The query is gated on
  // notifications.view so users who cannot see the item never
  // pay the round-trip.
  const hasNotifView = user !== null && hasPermission(user.permissions, PERMS.NOTIFICATIONS.VIEW);
  const { count: unreadCount } = useNotificationUnreadCount({
    enabled: hasNotifView,
  });
  const unreadBadge = formatBadge(unreadCount);

  return (
    <nav
      aria-label="Navegación principal"
      className={cn(
        'hidden h-full w-52 shrink-0 border-r border-border bg-card lg:flex lg:flex-col',
      )}
    >
      <div className="flex h-11 items-center gap-2 border-b border-border px-4">
        <div className="grid size-7 place-items-center rounded-md bg-primary text-primary-foreground">
          <svg
            viewBox="0 0 32 32"
            width="14"
            height="14"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            aria-hidden="true"
          >
            <path d="M9 9h14" />
            <path d="M9 16h14" />
            <path d="M9 23h9" />
          </svg>
        </div>
        <span className="text-sm font-semibold tracking-tight">GlobalesERP</span>
      </div>

      <div className="flex-1 overflow-y-auto p-2">
        {groups.map((group) => (
          <div key={group.id} className="flex flex-col gap-0.5 pb-1">
            <h3 className="px-3 pb-1 pt-3 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              {group.label}
            </h3>
            <ul className="flex flex-col gap-0.5">
              {group.items.map((item) => {
                const Icon = ICONS[item.iconName] ?? LayoutDashboard;
                const showUnreadBadge = item.to === '/notifications' && unreadBadge !== null;
                return (
                  <li key={item.to}>
                    <SidebarLink
                      item={item}
                      Icon={Icon}
                      unreadBadge={showUnreadBadge ? unreadBadge : null}
                    />
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </div>

      <div className="border-t border-border p-3 text-xs text-muted-foreground">
        <p className="font-mono tracking-tight">GlobalesERP · v0.1.0</p>
      </div>
    </nav>
  );
}

/**
 * SidebarLink — single nav row. Uses `useLocation` internally so
 * React Router re-renders ONLY this row when the route changes
 * (parent stays put). The active-state match is a custom prefix
 * matcher (see `useIsActive`) so deep-links under a top-level
 * entry keep the parent highlighted.
 */
function SidebarLink({
  item,
  Icon,
  unreadBadge,
}: {
  item: NavItem;
  Icon: LucideIcon;
  unreadBadge: string | null;
}): ReactElement {
  const isActive = useIsActive(item.to, item.end);
  return (
    <NavLink
      to={item.to}
      {...(item.end ? { end: true } : {})}
      className={cn(
        'group flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors',
        isActive
          ? 'bg-secondary font-medium text-secondary-foreground'
          : 'text-muted-foreground hover:bg-secondary/50 hover:text-foreground',
      )}
    >
      <Icon className="size-4 shrink-0" aria-hidden="true" />
      <span className="truncate">{item.label}</span>
      {unreadBadge && (
        <span className="ml-auto grid size-5 shrink-0 place-items-center rounded-full bg-destructive font-mono text-[10px] font-semibold tabular-nums text-destructive-foreground">
          <span className="sr-only">{`${unreadBadge} notificaciones no leídas`}</span>
          <span aria-hidden="true">{unreadBadge}</span>
        </span>
      )}
    </NavLink>
  );
}
